/* eslint-disable import/no-unresolved */
import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import moment from 'moment';
import { useSnackbar } from 'v3/components/Snackbar';
import { useModal } from 'hooks';
import { cooperplaceApi } from 'services/api';
import { fetchGenders } from 'utils/fetches';
import { FaImage } from 'react-icons/fa';
import { CIVIL_STATUS, HOME_TYPES, ADDRESS_TYPES } from 'v3/utils/masks';

import {
  Text,
  Card,
  Upload,
  CardToWarn,
  LoadingProgressBar
} from 'v3/components';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { validate } from './validator'
import GeneralData from './GeneralData';
import Contact from './Contact';
import CnhData from './CnhData';
import PaymentMethod from './PaymentMethod';
import Address from 'v3/pages/Driver/RegisterOcr/Address';
import ConfirmModal from './Modal/ConfirmModal';

import { TravelContext } from '../../controller';

const [, useTravel] = TravelContext;

const DriverRegisterOcr = forwardRef(({
  setData,
  data,
  handleClose,
  setSaveLoading,
  saveLoading,
  setErrors,
  errors,
}, ref) => {

  const snackbar = useSnackbar();
  const confirmModal = useModal();
  const travel = useTravel();

  const [frontImageCnh, setFrontImageCnh] = useState([]);
  const [uploadResetKey, setUploadResetKey] = useState(0);

  const [loading, setLoading] = useState(false);

  const [pendingOcrData, setPendingOcrData] = useState(null)
  const [genders, setGenders] = useState([])
  // applyOcrData roda num closure antigo (efeito do upload); o ref sempre tem a lista carregada
  const gendersRef = useRef([])

  async function updateAttachment(driverId) {
    try {

      const attachmentsFiles = new FormData();

      const fileName = frontImageCnh[0]?.name
        .replaceAll('_', '-')
        .split('.')
        .slice(0, -1)
        .join('.');

      const fileNameFormatted = `${Date.now()}.${frontImageCnh[0]?.type.split('/')[1]
        }`;
      const name = fileName
        ? `${fileName}-${fileNameFormatted}`
        : fileNameFormatted;
      const newFile = new File([frontImageCnh[0]], name, {
        type: frontImageCnh[0]?.type,
      });

      const type = frontImageCnh[0]?.type;
      const description = 'CNH - Carteira Nacional de Habilitação';
      attachmentsFiles.append('file', newFile);
      attachmentsFiles.append('type', type);
      attachmentsFiles.append('description', description);
      attachmentsFiles.append('isOcrImported', true);
      attachmentsFiles.append('dueDate', new Date().toISOString());

      await cooperplaceApi.post(
        `drivers/${driverId}/attachment`,
        attachmentsFiles
      );

    } catch (error) {
      snackbar.show(<Text>Não foi possível atualizar o anexo.</Text>, {
        type: 'error',
      });
    }
  }

  function applyOcrData(ocrData, formatOcrData, driver) {
    setData(old => ({
      ...old,
      generalData: {
        ...old.generalData,
        name: ocrData?.name,
        cpf: ocrData?.cpf,
        dateOfBirth: ocrData?.dateOfBirth ? moment(ocrData.dateOfBirth, 'DD/MM/YYYY').format('YYYY-MM-DD') : '',
        rgNumber: ocrData?.rgNumber,
        motherName: ocrData?.affiliation?.split('\n')[1],
        fatherName: ocrData?.affiliation?.split('\n')[0],
        rgOrganEmission: ocrData?.rgOrganEmission,
        rgOrganEmissionUf: formatOcrData?.rgOrganEmissionUf,
        cityOfBirth: formatOcrData?.cityOfBirth,
        isAutonomous: driver?.isAutonomous,
        pis: driver?.pispasep,
        rgDispatchDate: driver?.rgDispatchDate ? moment(driver.rgDispatchDate, 'YYYY-MM-DD').format('YYYY-MM-DD') : '',
        civilStatus: CIVIL_STATUS.find(item => item.value === driver?.civilStateId),
        gender: gendersRef.current.find(item => item.id === driver?.genderId) ?? old.generalData?.gender,
      },
      cnhData: {
        ...old.cnhData,
        cnhKey: driver?.cnhKey,
        cnhObservations: ocrData?.cnhObservations,
        cnhCategory: formatOcrData?.cnhCategory,
        cnhDispatchCity: formatOcrData?.cnhDispatchCity,
        cnhNumber: ocrData?.cnhNumber,
        cnhFirstCnhDate: ocrData?.cnhFirstCnhDate ? moment(ocrData.cnhFirstCnhDate, 'DD/MM/YYYY').format('YYYY-MM-DD') : '',
        cnhDateEmission: ocrData?.cnhDateEmission ? moment(ocrData.cnhDateEmission, 'DD/MM/YYYY').format('YYYY-MM-DD') : '',
        cnhDateOfExpiration: ocrData?.cnhDateOfExpiration ? moment(ocrData.cnhDateOfExpiration, 'DD/MM/YYYY').format('YYYY-MM-DD') : '',
      },
      contact: {
        email: driver?.email,
        phone: driver?.phoneCooperplace,
      },
      paymentMethod: driver?.driver?.payment,
      driverAddress: {
        ...old.driverAddress,
        address: driver?.address?.address ? driver.address.address : null,
        neighborhood: driver?.address?.neighborhood ? driver.address.neighborhood : null,
        city: driver?.address?.addressCity ? {
          id: driver.address.addressCity.id,
          name: driver.address.addressCity.name,
          ibgeCode: driver.address.addressCity.ibgeCode,
          province: {
            uf: driver.address.addressProvince.uf,
            name: driver.address.addressProvince.name
          }
        } : null,
        province: driver?.address?.addressProvince ? {
          uf: driver.address.addressProvince.uf,
          name: driver.address.addressProvince.name,
          ibgeCode: driver.address.addressProvince.ibgeCode,
        } : null,
        cep: driver?.address?.zipCode ? driver.address.zipCode : null,
        lat: driver?.address?.lat ? driver.address.lat : null,
        lng: driver?.address?.lng ? driver.address.lng : null,
        number: driver?.address?.number ? driver.address.number : null,
        numberless: !!driver?.address && !driver.address.number,
        complement: driver?.address?.complement ? driver.address.complement : null,
        homeType: driver?.propertyType ? HOME_TYPES.find(item => item.label === driver?.propertyType) : null,
        addressType: driver?.propertyStatus ? ADDRESS_TYPES.find(item => item.label === driver?.propertyStatus) : null,
      },
    }));
  }

  const extractOcrCnh = async (file) => {
    try {
      const formData = new FormData();
      formData.append('document', file);

      setLoading(true);
      const { data } = await cooperplaceApi.post('/drivers/extract/document/ocr', formData);

      const ocrData = data?.ocrData
      const driver = data?.driver
      const formatOcrData = data?.formatData
      const databaseData = data?.databaseData
      const confirmUpdate = data?.isUpdate

      if (confirmUpdate) {
        setPendingOcrData({ ocrData, formatOcrData, databaseData, driver });
        confirmModal.open();
      } else {
        applyOcrData(ocrData, formatOcrData, driver);
        snackbar.show(<Text>CNH processada com sucesso!</Text>, {
          type: 'success',
        });
      }

    } catch (err) {
      setFrontImageCnh([]);
      setUploadResetKey(k => k + 1);

      const message = err?.response?.data?.error;

      snackbar.show(<Text>{message || 'Erro ao processar a CNH'}</Text>, {
        type: 'error',
      });

      setLoading(false);

    } finally {
      setLoading(false);
    }
  }

  function validateCnhFile(file) {
    return file?.type === 'application/pdf' && file?.name?.toLowerCase().endsWith('.pdf');
  }

  useEffect(() => {
    fetchGenders().then(list => {
      gendersRef.current = list
      setGenders(list)
      if (!list.length) {
        snackbar.show(<Text>Não foi possível carregar as opções de sexo</Text>, {
          type: 'error',
        })
      }
    })
  }, [])

  useEffect(() => {
    if (frontImageCnh?.length) {
      const file = frontImageCnh[0];
      extractOcrCnh(file);
    }
  }, [frontImageCnh])


  async function handleRegister() {

    try {

      setSaveLoading(true);

      const isDataValid = await validate(
        data,
        setErrors
      );

      if (!isDataValid) {
        snackbar.show(<Text>Verifique os campos obrigatórios!</Text>, {
          type: 'error',
        });
        setLoading(false);
        return;
      }

      const payload = {
        ...data,
        driverAddress: {
          ...data.driverAddress,
          numberless: undefined,
          number: data.driverAddress?.numberless ? null : data.driverAddress?.number,
        },
      };

      const response = await cooperplaceApi.post('/drivers/update-or-create', payload);

      travel.setData(prev => ({
        ...prev,
        driver: {
          id: response.data.id,
          social_name: response.data.socialName,
          cnh_exp_date: response.data.cnhExpDate,
          cnh_number: response.data.cnhNumber,
        },
      }));

      if (frontImageCnh?.length && response?.data?.id) {
        await updateAttachment(response.data.id);
      }

      snackbar.show(<Text>Motorista registrado com sucesso!</Text>, {
        type: 'success',
      });

      handleClose();

    } catch (err) {

      const message = err?.response?.data?.error;

      snackbar.show(<Text>{message || 'Erro ao registrar o motorista'}</Text>, {
        type: 'error',
      });

      setSaveLoading(false);

    } finally {
      setSaveLoading(false);
    }
  }

  useImperativeHandle(ref, () => ({
    save: handleRegister,
  }));

  return (
    <>
      <div style={{ position: 'relative' }}>
        {(loading) && (
          <LoadingProgressBar text={loading ? 'Processando CNH...' : 'Registrando motorista...'} />
        )}
        <Row style={{ display: 'flex', width: '100%', justifyContent: 'flex-end' }}>
          <Col xl={5} lg={5} md={12} xs={12}>
            <CardToWarn
              text="Realize apenas o upload da CNH,
              a IA irá extrair automaticamente os dados para o cadastro do motorista.
              Após o processamento, revise as informações extraídas e preencha os campos restantes para concluir o cadastro."
            />
            <Card
              header={
                <Text color="#464E5F" type="header">
                  Documentos
                </Text>
              }
            >
              <Upload
                key={uploadResetKey}
                sizeIcon={50}
                icon={<FaImage size={50} />}
                disabled={saveLoading}
                onChange={event =>
                  setFrontImageCnh(event.target.files)
                }
                onFilesChange={files => {
                  if (!files?.length) {
                    setFrontImageCnh([]);

                    setData(old => ({
                      ...old,
                      generalData: {
                        ...old.generalData,
                        name: null,
                        cpf: null,
                        dateOfBirth: null,
                        rgNumber: null,
                        motherName: null,
                        fatherName: null,
                        rgOrganEmission: null,
                        rgOrganEmissionUf: null,
                        cityOfBirth: null,
                      },
                      cnhData: {
                        ...old.cnhData,
                        cnhCategory: null,
                        cnhDispatchCity: null,
                        cnhNumber: null,
                        cnhFirstCnhDate: null,
                        cnhDateEmission: null,
                        cnhDateOfExpiration: null,
                        cnhObservations: null,
                      }
                    }));
                  }
                }}
                value={frontImageCnh}
                id="imgCnhDriver"
                name="imgCnh"
                accept={['.pdf']}
                label="Arquivo máximo de 10MB"
                maxFileSize={10485760}
                showPreview={true}
                validate={validateCnhFile}
                onValidationError={() =>
                  snackbar.show(<Text>Apenas arquivos PDF são aceitos para o processamento.</Text>, {
                    type: 'error',
                  })
                }
              />
              <Col style={{ marginTop: 8 }}>
                {!frontImageCnh?.length && (
                  <CardToWarn
                    text="⛔ Apenas arquivos no formato PDF são aceitos. Faça o upload da CNH em PDF para continuar."
                    backgroundColor="#FDECEA"
                    borderColor="#F44336"
                    textColor="#C62828"
                  />
                )}
              </Col>
            </Card>
          </Col>

          <Col xl={7} lg={7} md={12} xs={12} key={frontImageCnh?.length ? 'with-image' : 'no-image'}>
            <GeneralData
              data={data?.generalData}
              setData={generalData => setData({ ...data, generalData })}
              disableFields={!frontImageCnh?.length}
              frontImageCnh={frontImageCnh}
              errors={errors}
              loading={saveLoading}
              genders={genders}
            />

            <CnhData
              data={data?.cnhData}
              setData={cnhData => setData({ ...data, cnhData })}
              disableFields={!frontImageCnh?.length}
              errors={errors}
              loading={saveLoading}
            />

            <PaymentMethod
              data={data?.paymentMethod}
              setData={paymentMethod => setData({ ...data, paymentMethod })}
              errors={errors}
              loading={saveLoading}
            />

            <Contact
              data={data?.contact}
              setData={contact => setData({ ...data, contact })}
              errors={errors}
              loading={saveLoading}
            />

            <Address
              data={data?.driverAddress}
              setData={driverAddress => setData({ ...data, driverAddress })}
              errors={errors}
            />
          </Col>
        </Row>
      </div>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={confirmModal.close}
        data={pendingOcrData?.databaseData}
        onConfirm={() => {
          if (pendingOcrData) {
            applyOcrData(pendingOcrData.ocrData, pendingOcrData.formatOcrData, pendingOcrData?.driver);
            snackbar.show(<Text>CNH processada com sucesso!</Text>, { type: 'success' });
            setPendingOcrData(null);
          }
          confirmModal.close();
        }}
        setFrontImageCnh={() => { setFrontImageCnh([]); setUploadResetKey(k => k + 1); }}
      />
    </>
  );
})

export default DriverRegisterOcr;
